import type { Lang } from "./LanguageContext";

/**
 * 한/영 번역 사전.
 *
 * 구조: STRINGS[lang][namespace][key] = "문자열"
 *  - 자리표시자는 "{name}" 형태로 작성하고 t(key, { name }) 로 치환한다.
 *  - 새 페이지를 영어화할 때 네임스페이스(common/main/svc/client/quick/visit/legal/report/footer/city)
 *    아래에 키를 추가한다. ko/en 양쪽에 같은 키를 둔다.
 *  - 누락 시 t()가 ko → key 원문 순으로 폴백하므로 화면이 깨지지 않는다.
 *  - svc 네임스페이스는 서비스 표시명. 내부 key(종합운세 등)는 그대로 두고 표시만 번역한다.
 */
type Dict = Record<string, Record<string, string>>;

const ko: Dict = {
  common: {
    langToggle: "EN",
    langName: "한국어",
    submit: "제출하기",
    loading: "처리 중...",
    name: "이름",
    gender: "성별",
    male: "남성",
    female: "여성",
    birthDate: "생년월일",
    birthTime: "태어난 시간",
    timeUnknown: "시간 모름",
    city: "출생 도시",
    email: "이메일",
  },
  svc: {
    종합운세: "종합운세",
    신년운세: "신년운세",
    연애운: "연애운",
    결혼운: "결혼운",
    재회운: "재회운",
    직업운: "직업운",
    사업운: "사업운",
    "사업(재물운)": "재물운",
    건강운: "건강운",
    연애궁합: "연애궁합",
    심층궁합: "심층궁합",
  },
  main: {
    modalTitleA: "우연 사주를 방문하신 것을",
    modalTitleB: "환영합니다",
    modalText1: "우연사주는 정통 명리학 기반 사주와 약간의 개인적 해석으로 다른 정통사주와는 다른 관점으로 풀이됨을 미리 알려드립니다.",
    modalText2: "정통 사주학 관점의 풀이를 보시는 분들은 다른 곳으로의 방문을 추천드립니다.",
    modalBtn: "확인했습니다",
    heroBadge: "후기 만점",
    brandName: "우연 사주",
    brandSub: "우연히 만난 내 인생의 지도",
    ratingNaver: "네이버 예약",
    ratingKakao: "카카오 예약",
    ratingChannels: "방문·전화·온라인",
    sectionServices: "서비스 선택",
    cookieLabel: "QUICK 간편하게! 저렴하게!",
    cookieTitle: "쿠키 사주",
    cookieDesc1: "저렴한 가격으로 빠르고 간단하게 사주보세요",
    cookieDesc2: "연애운 · 직장운 · 재물운 · 연애궁합",
    emailDelivery: "*이메일로 결과를 받아볼 수 있어요!",
    cookieBtn: "바로 확인하기 →",
    onlineLabel: "ONLINE · 상세 리포트",
    onlineTitle: "우연 사주",
    onlineDesc1: "종합운세 · 신년운세 · 연애궁합 · 결혼운 등",
    onlineDesc2: "8가지 운세 중 선택하여 심층 분석을 받으세요.",
    onlineBtn: "운세 선택하기 →",
    visitLabel: "VISIT · 1:1 대면 상담",
    visitTitle: "방문 상담 사주",
    visitDesc1: "직접 방문하여 1:1 대면 심층 상담을 받으세요.",
    visitDesc2: "종합운세 · 연애궁합 · 결혼운 등 9가지 운세",
    visitOnlyNote: "*방문 상담 하시는 분들만 입력해 주세요. 온라인 상담은 아닙니다.",
    visitBtn: "방문 접수하기 →",
    cupidLabel: "CUPID · 내 인연 찾기",
    cupidTitle: "우연의 화살",
    cupidDesc1: "여러 명이 각자 사주를 넣으면",
    cupidDesc2: "가장 잘 맞는 짝을 우연의 화살로 이어드려요",
    cupidBtn: "궁합 매칭 시작하기 →",
    sectionPhone: "전화 상담",
    phoneHeaderTitle: "전화 상담 예약",
    phoneHeaderSub: "전화 통화로 1:1 맞춤 상담",
    phoneCallBtn: "📞  {phone} 전화하기",
    phonePayLabel: "상담 예약 결제",
    priceLabel: "{price}원",
    payBtn: "💳  {price}원 결제하기",
    payName: "우연 사주 전화상담 — {svc}",
    payLoading: "❌ 결제 모듈 로딩 중입니다. 잠시 후 다시 시도하세요.",
    paySuccess: "✅ 결제 완료! {phone}로 연락드리겠습니다.",
    payFail: "❌ 결제 실패: {msg}",
    visitHeaderTitle: "방문 예약 (네이버·카카오)",
    visitHeaderSub: "날짜·시간 선택 후 방문",
    visitInfo1: "원하는 날짜와 시간을 선택하여 예약하세요.",
    visitInfo2: "결제는 방문 당일 현장에서 진행됩니다.",
    naverBook: "네이버에서 예약하기",
    kakaoBook: "카카오에서 예약하기",
  },
  visit: {
    errConsent: "개인정보 활용에 동의해주세요.",
    errRequired: "필수 항목(이름, 생년월일)을 모두 입력해주세요.",
    errBirthFormat: "생년월일을 YYYY/MM/DD 형식으로 입력해주세요. 예) 1990/05/15",
    errPartner: "상대방 이름과 생년월일을 입력해주세요.",
    errSubmit: "제출 중 오류가 발생했습니다.",
    doneBrand: "우연 사주",
    doneTitle: "방문 상담 접수 완료",
    doneDesc: "접수가 완료되었습니다.",
    doneEmail: "상담 후 분석 리포트를 {email}으로 보내드리겠습니다.",
    doneContact: "문의: wooyeonsaju@gmail.com",
    doneNewBtn: "새 접수하기",
    backHome: "← 홈",
    title: "우연이와 1:1 방문 상담",
    heroSub: "우연히 만난 내 인생의 지도",
    nameLabel: "이름 *",
    namePlaceholder: "홍길동",
    genderLabel: "성별 *",
    birthDateLabel: "생년월일 * (예: 1990/05/15)",
    birthTypeLabel: "양/음력",
    solar: "양력",
    lunar: "음력",
    lunarLeap: "음력 윤달",
    birthTimeLabel: "태어난 시간",
    timePlaceholder: "예: 14:30 또는 1430",
    cityLabel: "출생 도시 (시간 보정용)",
    emailLabel: "이메일 (리포트 수령용, 선택)",
    phoneLabel: "전화번호",
    serviceLabel: "상담 종류 *",
    compatTitle: "💕 상대방 정보 입력",
    partnerNameLabel: "상대방 이름 *",
    partnerGenderLabel: "상대방 성별 *",
    partnerBirthDateLabel: "상대방 생년월일 * (예: 1990/05/15)",
    partnerBirthTypeLabel: "상대방 양/음력",
    partnerBirthTimeLabel: "상대방 태어난 시간",
    partnerCityLabel: "상대방 출생 도시",
    memoLabel: "상담 요청 내용 (선택)",
    memoPlaceholder: "궁금하신 점이나 원하시는 상담 내용을 간단히 적어주세요.",
    consentText: "개인정보(생년월일, 이메일 등)를 사주 분석 및 서비스 제공 목적으로 수집·이용하는 것에 동의합니다. *",
    submitLoading: "접수 중...",
    submitBtn: "방문 상담 접수하기",
  },
  quick: {
    svcCareer: "직장운",
    svcWealth: "재물운",
    svcLoveDesc: "이상형을 어디서 만날까?\n어떻게 생겼을까? 언제 만날까?",
    svcCareerDesc: "이직은 언제?\n재직 중이라면 승진할까?",
    svcWealthDesc: "투자 성공할까?\n계약운, 사업 매출은?",
    svcCompatDesc: "우리 둘의 궁합점수는?",
    errConsent: "이용약관 및 개인정보 처리방침에 동의해주세요.",
    errName: "이름을 입력해주세요.",
    errBirthDate: "생년월일을 올바르게 입력해주세요. 예: 1990/05/15",
    errBirthMonth: "생월을 올바르게 입력해주세요.",
    errBirthDay: "생일을 올바르게 입력해주세요.",
    errEmailEmpty: "결과를 받을 이메일 주소를 입력해주세요.",
    errEmailInvalid: "올바른 이메일 주소를 입력해주세요.",
    errPartnerName: "상대방 이름을 입력해주세요.",
    errPartnerBirthDate: "상대방 생년월일을 올바르게 입력해주세요.",
    errPartnerBirthMonth: "상대방 생월을 올바르게 입력해주세요.",
    errPartnerBirthDay: "상대방 생일을 올바르게 입력해주세요.",
    errAgeLimit: "연애궁합은 만 20세 이상만 이용 가능합니다.",
    errAnalyze: "분석 중 오류가 발생했습니다.",
    payName: "우연 쿠키사주 — {svc}",
    payFail: "결제 실패: {msg}",
    payRetry: "다시 시도해주세요.",
    loadingAnalyze: "사주를 분석하는 중입니다",
    loadingReport: "결제 후 리포트 생성 중...",
    loadingSub: "명리학 기반으로 정밀 분석 중...",
    back: "← 돌아가기",
    previewTitle: "{svc} 미리보기",
    lockedNotice: "이 아래 내용은 결제 후 공개돼요",
    lockedDetail: "시기 · 상대 유형 · 주의 시점 등 전체 분석",
    getFullResult: "전체 결과 받기",
    sentToEmail: "결제 즉시 {email} 로 발송돼요",
    payFullBtn: "₩{price} — 전체 결과 받기",
    refundNoticeBefore: "결제 전 ",
    refundPolicy: "환불정책",
    refundNoticeAfter: " 확인 · 열람 전 7일 이내 전액 환불",
    home: "← 홈",
    brand: "쿠키사주",
    analyzeComplete: "분석 완료!",
    emailSentPrefix: "",
    emailSentSuffix: "으로 결과지가 발송되었습니다.",
    checkSpam: "메일이 안 보이면 스팸 폴더를 확인하세요.",
    analyzeDoneTitle: "분석이 완료되었습니다!",
    resultEmailed: "결과가 이메일로 발송되었습니다.",
    goMain: "메인으로 가기",
    seeCookieSaju: "쿠키사주 보러가기",
    surveyTitle: "출생시간 추정 설문",
    surveyDesc: "10개 문항으로 가능성 높은 시주를 추천해드립니다.",
    surveyForPartner: " (상대방 기준)",
    cancel: "취소",
    questionsLeft: "{n}개 질문 남음",
    estimateHour: "시주 추정하기",
    surveyResultTitle: "추정 결과 (가능성 높은 순)",
    surveyMeta: "오행: {ohaeng} · {animal}띠 · 일치도 {confidence}%",
    setThisTime: "이 시간으로 설정",
    retake: "다시 풀기",
    close: "닫기",
    heroSub: "궁금한 운세 하나만 딱 골라 명리학으로 풀어드립니다",
    selectService: "↩ 서비스 선택",
    heroPriceSub: "{price}원 · 결제 후 이메일 즉시 발송",
    chooseFortune: "궁금한 운세를 선택하세요",
    cardPrice: "{price}원",
    badgeService: "쿠키사주 {svc} — {price}원",
    ageLimitNote: "※ 만 20세 이상만 이용 가능",
    myInfo: "내 정보",
    partnerPrompt: "↕ 상대방 정보도 입력해주세요",
    partnerInfo: "상대방 정보",
    consentPrefix: "",
    consentMid: " 및 ",
    terms: "이용약관",
    privacy: "개인정보 처리방침",
    consentSuffix: "에 동의하며, 개인정보(이름, 생년월일)를 사주 분석 목적으로 수집·이용하는 것에 동의합니다. (필수)",
    previewNote: "미리보기 후 결제 · 결제 즉시 이메일로 결과 발송",
    refundNotice2Before: "결제 전 ",
    refundNotice2After: "을 확인해주세요. 리포트 열람 전 7일 이내 전액 환불 가능합니다.",
    basicInfo: "기본 정보",
    nameLabel: "이름 *",
    namePlaceholder: "홍길동",
    genderLabel: "성별 *",
    genderFemale: "여성",
    genderMale: "남성",
    emailLabel: "결과 받을 이메일 *",
    emailHint: "결제 완료 후 이메일로 결과지가 발송됩니다",
    birthDateTime: "생년월일시",
    typeLabel: "구분",
    birthDateLabel: "생년월일 * (예: 1990/05/15)",
    birthTimeLabel: "태어난 시간",
    timeUnknown: "시간 모름",
    estimateHelp: "✨ 시간 추정 도움받기",
    timePlaceholder: "예: 12:50",
    regionLabel: "지역 (시간 보정용)",
    birthTypeSolar: "양력",
    birthTypeLunar: "음력",
    birthTypeLunarLeap: "음력 윤달",
  },
  client: {
    errConsent: "개인정보 활용에 동의해주세요.",
    errEmail: "결과 리포트 수신을 위해 이메일 주소를 입력해주세요.",
    errRequired: "필수 항목(이름, 생년월일)을 모두 입력해주세요.",
    errBirthDate: "생년월일을 올바르게 입력해주세요. 예: 1990/05/15",
    errPartner: "상대방 정보를 모두 입력해주세요.",
    analysisCompleteText: "분석이 완료되었습니다.",
    errSubmit: "제출 중 오류가 발생했습니다.",
    alertPayLoading: "결제 모듈 로딩 중입니다. 잠시 후 다시 시도해주세요.",
    payName: "우연 사주 — {svc} 전체 리포트",
    alertPayProcessError: "결제는 완료되었으나 처리 중 오류가 발생했습니다. 관리자에게 문의해주세요.",
    alertPayFail: "결제 실패: {msg}",
    payRetry: "다시 시도해주세요.",
    loadingTitle: "사주를 분석하는 중입니다",
    loadingSub: "명리학 기반으로 정밀 분석 중...",
    brandName: "우연 사주",
    previewCompleteSub: "분석이 완료되었습니다",
    previewResultTitle: "{name}님의 분석 결과",
    reportSentPre: "",
    reportSentPost: "으로 리포트가 발송되었습니다!",
    viewFullReport: "전체 리포트 바로 보기",
    resetBtn: "다시 입력하기",
    brandTagline: "우연히 만난 내 인생의 지도",
    heroTitle: "우연 사주",
    home: "← 홈",
    analysisComplete: "{svc} 분석 완료",
    previewTeaseTitle: "{name}님의 운명 일부가 보입니다",
    lockTitle: "나머지 운명은 베일에 가려져 있습니다",
    lockSubLine1: "결제 후 전체 내용을 이메일로 즉시 받아보세요.",
    lockSubLine2: "아래 내용이 모두 포함됩니다.",
    payBtn: "₩{price} — 전체 운명 확인하기",
    payInfoPre: "결제 즉시 ",
    yourEmail: "입력한 이메일",
    payInfoPost: "로 발송 · 7일간 유효",
    refundPolicy: "환불정책",
    refundNote: "열람 전 7일 이내 전액 환불 가능",
    sectionPickFortune: "센스있는 사주 분석",
    hookComprehensive: "내 운명은 어떨까? '운의 파도'를 확인하세요.",
    hookNewYear: "새해, 운의 흐름을 먼저 읽는 자가 앞서 갑니다.",
    hookLove: "인연은 준비된 사람에게 찾아옵니다.",
    hookLoveCompat: "끌리는 이유가 있습니다. 우리는 인연일까?",
    hookDeepCompat: "결혼까지 가는 사람인지, 사주가 알고 있습니다.",
    hookCareer: "어떤 직업을 선택해야할까?",
    hookBusiness: "나의 타고난 재물운",
    hookHealth: "몸이 보내는 신호, 사주가 먼저 알고 있습니다.",
    subComprehensive: "타고난 명운과 인생 전체 흐름 정밀 분석",
    subNewYear: "올해 월별 운세 전체 흐름 분석",
    subLove: "인연을 만나는 시기와 상대방 특징 분석",
    subLoveCompat: "궁합, 첫만남은 언제일지 사주로 알려드립니다.",
    subDeepCompat: "결혼운·자녀운·이별위험시기 정밀 분석",
    subCareer: "커리어 전환·승진의 최적 시기 분석",
    subBusiness: "내 재물이 터지는 구체적인 시기 확인",
    subHealth: "취약 부위·사고수·수술수 시기 분석",
    viewService: "{svc} 보기",
    surveyTitle: "출생시간 추정 설문",
    surveyDesc: "10개 문항으로 가능성 높은 시주를 추천해드립니다.",
    surveyDescPartner: "상대방의 특성에 맞게 답해주세요.",
    cancel: "취소",
    questionsRemaining: "{count}개 질문 남음",
    estimateHour: "시주 추정하기",
    surveyResultTitle: "추정 결과 (가능성 높은 순)",
    surveyResultMeta: "오행: {ohaeng} · {animal}띠 · 일치도 {confidence}%",
    applyThisTime: "이 시간으로 설정",
    retake: "다시 풀기",
    close: "닫기",
    backToServices: "운세 선택으로 돌아가기",
    partnerRequiredNotice: "상대방 정보 필수 입력",
    subOptLabel: "세부 항목 선택 (복수 선택 가능)",
    minorNotice: "· 미성년({age}세) — 항목 자동 전환",
    sectionBasicInfo: "기본 정보",
    labelName: "이름 *",
    placeholderName: "홍길동",
    labelGender: "성별 *",
    optionFemale: "여성",
    optionMale: "남성",
    labelEmail: "이메일 * — 결과 리포트가 이메일로 발송됩니다",
    birthType_solar: "양력",
    birthType_lunar: "음력",
    birthType_lunar_leap: "음력 윤달",
    labelBirthType: "구분",
    labelBirthDate: "생년월일 * (예: 1990/05/15)",
    labelBirthTime: "태어난 시간",
    timeUnknown: "시간 모름",
    estimateHelp: "시간 추정 도움받기",
    placeholderTime: "예: 14:30 또는 1430",
    sectionRegion: "지역 (시간 보정용)",
    partnerInfo: "상대방 정보",
    partnerSubLove: "연애궁합: 첫 만남 년도·속궁합·성격궁합·한난조습까지 분석합니다",
    partnerSubDeep: "심층궁합: 결혼운·자녀운·이별위험시기까지 정밀 분석합니다",
    labelPartnerName: "상대방 이름 *",
    placeholderPartnerName: "김영희",
    labelRegion: "지역",
    consentPre: "",
    linkTerms: "이용약관",
    consentAnd: "및",
    linkPrivacy: "개인정보 처리방침",
    consentText: "에 동의하며, 개인정보(이름, 생년월일)를 사주 분석 목적으로 수집·이용하는 것에 동의합니다. (필수)",
    submitting: "접수 중...",
    submitService: "{svc} 미리보기",
    sectionBirth: "생년월일시",
  },
  report: {
    loading: "결과를 불러오는 중...",
    loadError: "결과를 불러올 수 없습니다.",
    heroTitle: "우연 사주 리포트",
    nameHonorific: "{name}님",
    iframeTitle: "사주 분석 결과",
    basicInfo: "기본 정보",
    name: "이름",
    gender: "성별",
    female: "여성",
    male: "남성",
    femaleShort: "여",
    maleShort: "남",
    birthDate: "생년월일",
    birthplace: "출생지역",
    partner: "상대방",
    partnerBirth: "상대방 생일",
    partnerDefault: "상대방",
    accessPeriodLabel: "리포트 열람 기간: ",
    accessPeriodSuffix: " 까지",
    expiryUrgentMsg: "열람 기간이 얼마 남지 않았습니다. 지금 바로 PDF로 저장해 두세요.",
    expiryNormalMsg: "열람 기간 내 PDF 저장 버튼을 이용해 리포트를 보관하세요.",
    storyTitleDeepCompat: "끌림의 이유가 있습니다. 사주로 증명합니다.",
    storySubDeepCompat: "오행 배합·십성 궁합 정밀 분석",
    storyTitleLoveCompat: "두 사람, 정말 잘 맞을까요?",
    storyTitleLove: "그 사람, 지금 당신을 어떻게 보고 있을까요?",
    storySubLove: "감정의 흐름과 최적 타이밍 분석",
    storyTitleNewYear: "올해, 당신에게 어떤 일이 생길까요?",
    storySubNewYear: "월별 운세 흐름과 행운의 키워드",
    storyTitleBusiness: "지금 사업·투자 타이밍이 맞는 걸까요?",
    storySubBusiness: "재물이 터지는 구체적인 시기 확인",
    storySubWealth: "내 재물이 터지는 구체적인 시기 확인",
    storyTitleCareer: "이직·승진, 지금이 맞는 타이밍인가요?",
    storySubCareer: "커리어 전환과 상승 타이밍 정밀 분석",
    storyTitleHealth: "몸이 보내는 신호, 지금 놓치고 있지 않으신가요?",
    storySubHealth: "체질별 건강 취약 시기와 관리법 분석",
    storyTitleComprehensive: "당신의 사주, 지금 어떤 흐름 위에 있나요?",
    storySubComprehensive: "타고난 명운과 앞으로의 인생 흐름 분석",
    scenarioRejoin0: "{ptName}의 마음이 다시 열리는 시기가 있습니다. 두 사람의 운이 가장 잘 맞물리는 골든타임을 찾아 그 때 자연스럽게 연락을 시도하는 것이 중요합니다.",
    scenarioRejoin1: "서두르지 마세요. 상대방이 혼자만의 시간을 갖고 싶어 하는 시기에는 연락이 오히려 역효과를 냅니다. 때를 기다리는 것이 가장 현명한 전략입니다.",
    scenarioRejoin2: "짧고 가벼운 안부 한 마디가 긴 편지보다 훨씬 효과적입니다. 부담 없는 접근으로 시작하세요.",
    scenarioMarry0: "두 사람의 운이 결혼을 향해 맞물리는 시기가 있습니다. {name}님의 결혼 운이 가장 강하게 들어오는 연도와 달을 확인하세요.",
    scenarioMarry1: "결혼 이야기는 타이밍이 전부입니다. 상대방이 미래를 함께 계획하고 싶어 하는 시기에 자연스럽게 대화를 나누는 것이 좋습니다.",
    scenarioMarry2: "경제적 안정과 신뢰가 쌓인 이후 결혼을 진행하면 훨씬 안정적인 출발이 됩니다.",
    scenarioDeep0: "두 사람의 사주 오행 배합과 성격 궁합을 정밀 분석합니다. 끌리는 이유와 갈등이 생기는 패턴을 확인할 수 있습니다.",
    scenarioDeep1: "서로가 서로에게 힘이 되는 관계인지, 아니면 서로를 소모하는 관계인지 장기적인 궁합의 핵심을 파악할 수 있습니다.",
    scenarioDeep2: "갈등이 커지는 시기를 미리 알고 그 전에 소통을 강화하면 관계를 훨씬 오래, 건강하게 유지할 수 있습니다.",
    scenarioBiz0: "재물이 들어오는 시기와 나가는 시기가 사주마다 다릅니다. 언제 공격적으로 움직이고 언제 지출을 줄여야 하는지 파악하는 것이 핵심입니다.",
    scenarioBiz1: "재물 운이 강한 달에 계약과 영업을 집중하고, 운이 약한 시기에는 비용 관리와 리스크 최소화에 집중하세요.",
    scenarioBiz2: "동업은 운이 서로 맞는 시기에 시작해야 합니다. 운이 충돌하는 시기의 동업 계약은 손실로 이어질 수 있습니다.",
    scenarioCareer0: "승진·이직·취업의 기회가 찾아오는 시기가 정해져 있습니다. 그 시기를 미리 알고 준비하면 훨씬 높은 성공률을 기대할 수 있습니다.",
    scenarioCareer1: "직장에서 인정받고 도약할 수 있는 타이밍을 놓치지 마세요. 커리어 변화는 준비된 사람이 운을 만났을 때 성사됩니다.",
    scenarioCareer2: "무리한 이직보다는 내부에서 성과를 쌓은 뒤 더 좋은 기회를 잡는 것이 장기적으로 유리할 수 있습니다.",
    payModuleLoading: "결제 모듈 로딩 중입니다. 새로고침 후 시도해주세요.",
    paymentName: "우연 사주 — {svc} 핵심 날짜 공개",
    payProcessError: "결제는 완료되었으나 처리 중 오류가 발생했습니다. 관리자에게 문의해주세요.",
    payFailed: "결제 실패: {msg}",
    tryAgain: "다시 시도해주세요.",
    dateTitleRejoin: "{ptName}와 재회할 수 있는 날은?",
    dateTitleMarry: "두 사람의 결혼 골든타임",
    dateTitleBusiness: "재물 터지는 계약 최적일",
    dateTitleCareer: "합격·이직 성공 최적일",
    dateSubWithPartner: "{name}님 용신({useGod}) + {ptName} 일주 합(合) 분석",
    dateSubSelf: "{name}님 용신({useGod}) 기반 최적 시기 분석",
    emotionalConflictDays: "감정 충돌 위험일 (이별 위기)",
    dateGanzhi: "{y}년 {m}월 {d}일 ({gan}{ji}일)",
    giChungDay: "— 기신({avoidGod}) 충(沖) 집중일",
    top3Rejoin: "재회 가능성 높은 날 TOP 3",
    top3Marry: "결혼 합의 최적일 TOP 3",
    top3Business: "계약·시작 최적일 TOP 3",
    top3Career: "합격·승진 최적일 TOP 3",
    dateYmd: "{y}년 {m}월 {d}일",
    unlockAfterPayment: "결제 후 공개",
    dateActiveDesc: "{gan}{ji}일 · 용신({useGod}) 활성",
    partnerDayHap: " · {ptName} 일주 합(合)",
    monthScoreKeyDate: "월 운세 점수 {score}점 · 핵심 날짜",
    favorableJobs: "용신({useGod}) 기반 유리한 직종",
    favorableBiz: "용신({useGod}) 기반 유리한 업종",
    payCtaQuestion: "위 날짜의 정확한 연·월·일이 궁금하신가요?",
    payCtaReveal: "결제 후 즉시 모든 날짜가 공개됩니다.",
    payButton: "₩9,900 결제하고 날짜 확인하기 →",
    careerWood0: "교육·강사",
    careerWood1: "한의·의료",
    careerWood2: "출판·저술",
    careerWood3: "디자인·인테리어",
    careerWood4: "법률·상담",
    careerFire0: "미디어·방송",
    careerFire1: "IT·소프트웨어",
    careerFire2: "요식업·카페",
    careerFire3: "마케팅·광고",
    careerFire4: "연예·예술",
    careerEarth0: "부동산·건설",
    careerEarth1: "금융·투자",
    careerEarth2: "유통·물류",
    careerEarth3: "공무원·행정",
    careerEarth4: "농업·식품",
    careerMetal0: "제조·기계",
    careerMetal1: "금융·보험",
    careerMetal2: "경찰·군인",
    careerMetal3: "IT하드웨어",
    careerMetal4: "의료기기",
    careerWater0: "무역·수출입",
    careerWater1: "관광·여행",
    careerWater2: "의약·화학",
    careerWater3: "연구·R&D",
    careerWater4: "철학·역학",
    bizWood0: "교육사업",
    bizWood1: "인테리어·리모델링",
    bizWood2: "출판·콘텐츠",
    bizWood3: "의료·헬스케어",
    bizWood4: "환경·에코",
    bizFire0: "식음료·카페",
    bizFire1: "IT·앱개발",
    bizFire2: "미디어·유튜브",
    bizFire3: "미용·뷰티",
    bizFire4: "엔터테인먼트",
    bizEarth0: "부동산 중개",
    bizEarth1: "건설·시공",
    bizEarth2: "프랜차이즈 유통",
    bizEarth3: "식자재 도매",
    bizEarth4: "컨설팅",
    bizMetal0: "금속·기계 제조",
    bizMetal1: "귀금속·주얼리",
    bizMetal2: "IT하드웨어",
    bizMetal3: "경비·보안",
    bizMetal4: "법률·회계",
    bizWater0: "수입·무역",
    bizWater1: "온라인 쇼핑몰",
    bizWater2: "여행사·관광",
    bizWater3: "주류·음료",
    bizWater4: "물류·배송",
    totalCompatScore: "종합 궁합 점수",
    personalityCompat: "성격궁합",
    intimacyCompat: "속궁합",
    scorePts: "{n}점",
    bestMarriageTiming: "결혼 최적 시기",
    childrenFortune: "자녀운",
    firstChildTiming: "첫 자녀 가능 시기: {timing}",
    childGenderPrediction: "자녀 성별 예측: {gender}",
    ohaengCompat: "오행궁합",
    ohaengAnalysis: "오행 분석",
    hapTransformApplied: "합·변환 적용",
    generation: "→ 생(生)",
    control: "→ 극(剋)",
    hannanjoseupCompat: "한난조습 궁합",
    hannanLabel: "{name} 한난",
    joseupLabel: "{name} 조습",
    hannanComplementary: "한난 상보적 ✓",
    hannanSimilar: "한난 유사형 ⚠️",
    joseupComplementary: "조습 상보적 ✓",
    joseupSimilar: "조습 유사형 ⚠️",
    themeLoveFull: "연애운",
    themeMarryFull: "결혼운",
    themeBusinessFull: "사업운",
    themeCareerFull: "직업운",
    themeHealthFull: "건강운",
    themeWealthFull: "재물운",
    themeLove: "연애",
    themeMarry: "결혼",
    themeBusiness: "사업",
    themeCareer: "직업",
    themeHealth: "건강",
    themeWealth: "재물",
    saeunDetail: "세운 상세 분석",
    wolunTitle: "월운 (1~12월)",
    wolunTapHint: "· 월 탭하면 일운 달력",
    weekSun: "일",
    weekMon: "월",
    weekTue: "화",
    weekWed: "수",
    weekThu: "목",
    weekFri: "금",
    weekSat: "토",
    ilunCalendar: "일운 달력 — {y}년 {m}월",
    ganziMonth: "({gan}{ji}월)",
    legendYongil: "용신일",
    legendJohuil: "조후일",
    legendGiil: "기신일",
    legendNeutral: "중립일",
    counselorComment: "상담사 코멘트",
    toMain: "메인으로",
    viewOtherReadings: "다른 우연사주 보러가기",
    savePdf: "PDF 저장",
  },
  footer: {
    lblBizName: "상호명",
    valBizName: "우연",
    lblRep: "대표자",
    valRep: "김나연",
    lblBizNo: "사업자등록번호",
    lblMailOrder: "통신판매업신고번호",
    valMailOrder: "제 2026-서울영등포-1388호",
    lblContact: "연락처",
    lblAddress: "사업장 주소",
    valAddress: "서울시 영등포구 도림로 125가길 14 1층 우연사주",
    lblEmail: "이메일",
    linkTerms: "이용약관",
    linkPrivacy: "개인정보 처리방침",
    linkRefund: "환불정책",
  },
  city: {
    placeholder: "도시명 입력 (예: 서울특별시, 도쿄)",
    hintPre: "태어난 도시를 모르시는 경우 ",
    hintStrong: "'서울특별시'",
    hintPost: " 혹은 태어난 국가의 수도를 입력해주세요.",
  },
};

const en: Dict = {
  common: {
    langToggle: "한",
    langName: "English",
    submit: "Submit",
    loading: "Processing...",
    name: "Name",
    gender: "Gender",
    male: "Male",
    female: "Female",
    birthDate: "Date of birth",
    birthTime: "Birth time",
    timeUnknown: "Time unknown",
    city: "City of birth",
    email: "Email",
  },
  svc: {
    종합운세: "Comprehensive Reading",
    신년운세: "New Year Reading",
    연애운: "Love Fortune",
    결혼운: "Marriage Fortune",
    재회운: "Reunion Fortune",
    직업운: "Career Fortune",
    사업운: "Business & Wealth",
    "사업(재물운)": "Wealth Fortune",
    건강운: "Health Fortune",
    연애궁합: "Love Compatibility",
    심층궁합: "Deep Compatibility",
  },
  main: {
    modalTitleA: "Welcome to",
    modalTitleB: "Wooyeon Saju",
    modalText1: "Wooyeon Saju blends traditional Korean Myeongri (Four Pillars) astrology with a touch of personal interpretation, so our readings may take a different view from strictly orthodox saju.",
    modalText2: "If you are looking for a strictly traditional saju interpretation, we kindly suggest visiting another reader.",
    modalBtn: "I understand",
    heroBadge: "⭐ Top Rated",
    brandName: "Wooyeon Saju",
    brandSub: "The map of life you meet by chance",
    ratingNaver: "🟢 Naver Booking",
    ratingKakao: "💛 Kakao Booking",
    ratingChannels: "🐉 Visit · Phone · Online",
    sectionServices: "✨ Choose a Service",
    cookieLabel: "QUICK · Easy & Affordable!",
    cookieTitle: "Cookie Saju",
    cookieDesc1: "See your saju fast and simple, at a low price",
    cookieDesc2: "Love · Career · Wealth · Compatibility",
    emailDelivery: "*You'll get your results by email!",
    cookieBtn: "See it now →",
    onlineLabel: "ONLINE · Detailed Report",
    onlineTitle: "Wooyeon Saju",
    onlineDesc1: "Comprehensive, New Year, Compatibility, Marriage and more",
    onlineDesc2: "Choose from 8 readings for an in-depth analysis.",
    onlineBtn: "Choose a reading →",
    visitLabel: "VISIT · 1:1 In-Person",
    visitTitle: "In-Person Saju",
    visitDesc1: "Visit us for a 1:1 in-person, in-depth consultation.",
    visitDesc2: "9 readings including Comprehensive, Compatibility and Marriage",
    visitOnlyNote: "*For in-person visit consultations only. This is not for online consultations.",
    visitBtn: "Book a visit →",
    cupidLabel: "CUPID · Find Your Match",
    cupidTitle: "Wooyeon's Arrow",
    cupidDesc1: "Everyone enters their birth info,",
    cupidDesc2: "and we link the best-matched pairs with Wooyeon's arrow",
    cupidBtn: "Start matching →",
    sectionPhone: "📞 Phone Consultation",
    phoneHeaderTitle: "Book a Phone Consultation",
    phoneHeaderSub: "Personalized 1:1 consultation by phone",
    phoneCallBtn: "📞  Call {phone}",
    phonePayLabel: "Pay for your booking",
    priceLabel: "₩{price}",
    payBtn: "💳  Pay ₩{price}",
    payName: "Wooyeon Saju Phone Consultation — {svc}",
    payLoading: "❌ The payment module is still loading. Please try again shortly.",
    paySuccess: "✅ Payment complete! We'll reach out to you at {phone}.",
    payFail: "❌ Payment failed: {msg}",
    visitHeaderTitle: "Book a Visit (Naver · Kakao)",
    visitHeaderSub: "Pick a date & time, then visit",
    visitInfo1: "Choose your preferred date and time to book.",
    visitInfo2: "Payment is made on-site on the day of your visit.",
    naverBook: "🟢 Book on Naver",
    kakaoBook: "💛 Book on Kakao",
  },
  visit: {
    errConsent: "Please agree to the use of your personal information.",
    errRequired: "Please fill in all required fields (name and date of birth).",
    errBirthFormat: "Please enter your date of birth in YYYY/MM/DD format. e.g. 1990/05/15",
    errPartner: "Please enter your partner's name and date of birth.",
    errSubmit: "Something went wrong while submitting. Please try again.",
    doneBrand: "Wooyeon Saju",
    doneTitle: "Visit Consultation Received",
    doneDesc: "Your request has been received.",
    doneEmail: "After your consultation, we'll send your analysis report to {email}.",
    doneContact: "Contact: wooyeonsaju@gmail.com",
    doneNewBtn: "Start a new request",
    backHome: "← Home",
    title: "1:1 In-Person Saju with Wooyeon",
    heroSub: "The map of life you meet by chance",
    nameLabel: "Name *",
    namePlaceholder: "Hong Gil-dong",
    genderLabel: "Gender *",
    birthDateLabel: "Date of birth * (e.g. 1990/05/15)",
    birthTypeLabel: "Calendar",
    solar: "Solar",
    lunar: "Lunar",
    lunarLeap: "Lunar (leap month)",
    birthTimeLabel: "Birth time",
    timePlaceholder: "e.g. 14:30 or 1430",
    cityLabel: "City of birth (for time correction)",
    emailLabel: "Email (to receive your report, optional)",
    phoneLabel: "Phone number",
    serviceLabel: "Consultation type *",
    compatTitle: "💕 Partner's information",
    partnerNameLabel: "Partner's name *",
    partnerGenderLabel: "Partner's gender *",
    partnerBirthDateLabel: "Partner's date of birth * (e.g. 1990/05/15)",
    partnerBirthTypeLabel: "Partner's calendar",
    partnerBirthTimeLabel: "Partner's birth time",
    partnerCityLabel: "Partner's city of birth",
    memoLabel: "Consultation request (optional)",
    memoPlaceholder: "Briefly share any questions or what you'd like the consultation to cover.",
    consentText: "I consent to the collection and use of my personal information (date of birth, email, etc.) for saju analysis and service delivery. *",
    submitLoading: "Submitting...",
    submitBtn: "Submit visit request",
  },
  quick: {
    svcCareer: "Career Fortune",
    svcWealth: "Wealth Fortune",
    svcLoveDesc: "Where will you meet your ideal type?\nWhat will they look like? When will it happen?",
    svcCareerDesc: "When should you change jobs?\nWill you get promoted where you are?",
    svcWealthDesc: "Will your investments pay off?\nContract luck and business revenue?",
    svcCompatDesc: "What's our compatibility score?",
    errConsent: "Please agree to the Terms of Service and Privacy Policy.",
    errName: "Please enter your name.",
    errBirthDate: "Please enter a valid date of birth. e.g. 1990/05/15",
    errBirthMonth: "Please enter a valid birth month.",
    errBirthDay: "Please enter a valid birth day.",
    errEmailEmpty: "Please enter the email address to receive your results.",
    errEmailInvalid: "Please enter a valid email address.",
    errPartnerName: "Please enter your partner's name.",
    errPartnerBirthDate: "Please enter a valid date of birth for your partner.",
    errPartnerBirthMonth: "Please enter a valid birth month for your partner.",
    errPartnerBirthDay: "Please enter a valid birth day for your partner.",
    errAgeLimit: "Love Compatibility is available to ages 20 and over only.",
    errAnalyze: "Something went wrong during analysis.",
    payName: "Wooyeon Cookie Saju — {svc}",
    payFail: "Payment failed: {msg}",
    payRetry: "Please try again.",
    loadingAnalyze: "Analyzing your saju",
    loadingReport: "Generating your report after payment...",
    loadingSub: "Running a detailed Myeongri analysis...",
    back: "← Back",
    previewTitle: "{svc} Preview",
    lockedNotice: "The rest is revealed after payment",
    lockedDetail: "Full analysis: timing, partner type, key dates and more",
    getFullResult: "Get the full reading",
    sentToEmail: "Sent to {email} right after payment",
    payFullBtn: "₩{price} — Get the full reading",
    refundNoticeBefore: "Check the ",
    refundPolicy: "refund policy",
    refundNoticeAfter: " before paying · Full refund within 7 days before opening",
    home: "← Home",
    brand: "Cookie Saju",
    analyzeComplete: "Analysis complete!",
    emailSentPrefix: "Your report has been sent to ",
    emailSentSuffix: ".",
    checkSpam: "If you don't see it, please check your spam folder.",
    analyzeDoneTitle: "Your analysis is complete!",
    resultEmailed: "The results have been sent to your email.",
    goMain: "Go to home",
    seeCookieSaju: "Explore Cookie Saju",
    surveyTitle: "Birth Time Estimation Survey",
    surveyDesc: "We'll recommend the most likely birth-hour pillar based on 10 questions.",
    surveyForPartner: " (for your partner)",
    cancel: "Cancel",
    questionsLeft: "{n} question(s) left",
    estimateHour: "Estimate birth hour",
    surveyResultTitle: "Estimated results (most likely first)",
    surveyMeta: "Element: {ohaeng} · {animal} sign · Match {confidence}%",
    setThisTime: "Use this time",
    retake: "Retake",
    close: "Close",
    heroSub: "Pick the one fortune you're curious about and we'll read it through Myeongri.",
    selectService: "↩ Choose service",
    heroPriceSub: "₩{price} · Emailed instantly after payment",
    chooseFortune: "Choose the fortune you're curious about",
    cardPrice: "₩{price}",
    badgeService: "Cookie Saju {svc} — ₩{price}",
    ageLimitNote: "※ Available to ages 20 and over only",
    myInfo: "My Info",
    partnerPrompt: "↕ Please enter your partner's details too",
    partnerInfo: "Partner's Info",
    consentPrefix: "I agree to the ",
    consentMid: " and ",
    terms: "Terms of Service",
    privacy: "Privacy Policy",
    consentSuffix: ", and consent to the collection and use of my personal information (name, date of birth) for the purpose of saju analysis. (Required)",
    previewNote: "Preview first, then pay · Results emailed instantly after payment",
    refundNotice2Before: "Please review our ",
    refundNotice2After: " before paying. A full refund is available within 7 days before opening the report.",
    basicInfo: "Basic Info",
    nameLabel: "Name *",
    namePlaceholder: "John Smith",
    genderLabel: "Gender *",
    genderFemale: "Female",
    genderMale: "Male",
    emailLabel: "Email for results *",
    emailHint: "Your report will be emailed once payment is complete.",
    birthDateTime: "Birth Date & Time",
    typeLabel: "Type",
    birthDateLabel: "Date of birth * (e.g. 1990/05/15)",
    birthTimeLabel: "Birth time",
    timeUnknown: "Time unknown",
    estimateHelp: "✨ Help me estimate",
    timePlaceholder: "e.g. 12:50",
    regionLabel: "Region (for time correction)",
    birthTypeSolar: "Solar",
    birthTypeLunar: "Lunar",
    birthTypeLunarLeap: "Lunar (leap month)",
  },
  client: {
    errConsent: "Please agree to the use of your personal information.",
    errEmail: "Please enter your email address to receive the result report.",
    errRequired: "Please fill in all required fields (name, date of birth).",
    errBirthDate: "Please enter a valid date of birth. e.g. 1990/05/15",
    errPartner: "Please enter all of your partner's information.",
    analysisCompleteText: "Your analysis is complete.",
    errSubmit: "An error occurred during submission.",
    alertPayLoading: "The payment module is still loading. Please try again in a moment.",
    payName: "Woo-yeon Saju — {svc} Full Report",
    alertPayProcessError: "Payment was completed, but an error occurred during processing. Please contact the administrator.",
    alertPayFail: "Payment failed: {msg}",
    payRetry: "Please try again.",
    loadingTitle: "Analyzing your Four Pillars",
    loadingSub: "Performing a precise analysis based on Myeongri astrology...",
    brandName: "Woo-yeon Saju",
    previewCompleteSub: "Your analysis is complete",
    previewResultTitle: "{name}'s Analysis Results",
    reportSentPre: "Your report has been sent to ",
    reportSentPost: "!",
    viewFullReport: "View the Full Report",
    resetBtn: "Start Over",
    brandTagline: "The map of the life you stumbled upon",
    heroTitle: "Wooyeon Saju",
    home: "← Home",
    analysisComplete: "{svc} Analysis Complete",
    previewTeaseTitle: "A glimpse of {name}'s destiny is revealed",
    lockTitle: "The rest of your destiny remains behind the veil",
    lockSubLine1: "Pay now and receive the full report by email instantly.",
    lockSubLine2: "Everything below is included.",
    payBtn: "₩{price} — Reveal Your Full Destiny",
    payInfoPre: "Sent instantly to ",
    yourEmail: "the email you entered",
    payInfoPost: " · valid for 7 days",
    refundPolicy: "Refund Policy",
    refundNote: "Full refund available within 7 days before viewing",
    sectionPickFortune: "Clever saju analysis",
    hookComprehensive: "What does my destiny hold? Check your 'tide of fortune.'",
    hookNewYear: "In the new year, those who read the flow of fortune first stay ahead.",
    hookLove: "Love comes to those who are ready.",
    hookLoveCompat: "There's a reason you're drawn to each other. Are we meant to be?",
    hookDeepCompat: "Whether this is the person you'll marry — your Saju already knows.",
    hookCareer: "Which career should I choose?",
    hookBusiness: "Your inborn wealth fortune",
    hookHealth: "The signals your body sends — your Saju knows them first.",
    subComprehensive: "A precise analysis of your innate destiny and the overall flow of your life",
    subNewYear: "Analysis of this year's month-by-month fortune flow",
    subLove: "Analysis of when you'll meet your match and their characteristics",
    subLoveCompat: "We'll tell you your compatibility and when you'll first meet, through Saju.",
    subDeepCompat: "Precise analysis of marriage luck, children luck, and breakup-risk periods",
    subCareer: "Analysis of the optimal timing for career changes and promotions",
    subBusiness: "Find out the specific timing when your wealth will flourish",
    subHealth: "Analysis of vulnerable areas and the timing of accidents and surgeries",
    viewService: "View {svc}",
    surveyTitle: "Birth Time Estimation Survey",
    surveyDesc: "We'll recommend the most likely hour pillar based on 10 questions.",
    surveyDescPartner: "Please answer according to your partner's characteristics.",
    cancel: "Cancel",
    questionsRemaining: "{count} questions remaining",
    estimateHour: "Estimate Hour Pillar",
    surveyResultTitle: "Estimated Results (most likely first)",
    surveyResultMeta: "Five Elements: {ohaeng} · {animal} zodiac · Match {confidence}%",
    applyThisTime: "Use this time",
    retake: "Retake",
    close: "Close",
    backToServices: "Back to fortune selection",
    partnerRequiredNotice: "Partner information is required",
    subOptLabel: "Select detailed options (multiple choices allowed)",
    minorNotice: "· Minor (age {age}) — options switched automatically",
    sectionBasicInfo: "Basic Information",
    labelName: "Name *",
    placeholderName: "Hong Gil-dong",
    labelGender: "Gender *",
    optionFemale: "Female",
    optionMale: "Male",
    labelEmail: "Email * — the result report will be sent to this address",
    birthType_solar: "Solar",
    birthType_lunar: "Lunar",
    birthType_lunar_leap: "Lunar (leap month)",
    labelBirthType: "Type",
    labelBirthDate: "Date of Birth * (e.g. 1990/05/15)",
    labelBirthTime: "Time of Birth",
    timeUnknown: "Time unknown",
    estimateHelp: "Get help estimating the time",
    placeholderTime: "e.g. 14:30 or 1430",
    sectionRegion: "Region (for time correction)",
    partnerInfo: "Partner Information",
    partnerSubLove: "Love compatibility: we analyze the year of your first meeting, physical and personality compatibility, and warm/cold energy balance",
    partnerSubDeep: "Deep compatibility: a precise analysis including marriage luck, children luck, and breakup-risk periods",
    labelPartnerName: "Partner's Name *",
    placeholderPartnerName: "Kim Young-hee",
    labelRegion: "Region",
    consentPre: "I agree to the ",
    linkTerms: "Terms of Service",
    consentAnd: "and",
    linkPrivacy: "Privacy Policy",
    consentText: ", and I consent to the collection and use of my personal information (name, date of birth) for the purpose of Saju analysis. (required)",
    submitting: "Submitting...",
    submitService: "{svc} Preview",
    sectionBirth: "Date & Time of Birth",
  },
  report: {
    loading: "Loading your result...",
    loadError: "Unable to load the result.",
    heroTitle: "Wooyeon Saju Report",
    nameHonorific: "{name}",
    iframeTitle: "Saju analysis result",
    basicInfo: "Basic Information",
    name: "Name",
    gender: "Gender",
    female: "Female",
    male: "Male",
    femaleShort: "F",
    maleShort: "M",
    birthDate: "Date of Birth",
    birthplace: "Birthplace",
    partner: "Partner",
    partnerBirth: "Partner's Birthday",
    partnerDefault: "Partner",
    accessPeriodLabel: "Report available until ",
    accessPeriodSuffix: "",
    expiryUrgentMsg: "Your access period is almost over. Save it as a PDF right now.",
    expiryNormalMsg: "Use the Save as PDF button within the access period to keep your report.",
    storyTitleDeepCompat: "There's a reason for the attraction — and your Saju proves it.",
    storySubDeepCompat: "In-depth analysis of five-element balance and Ten-God compatibility",
    storyTitleLoveCompat: "Are the two of you really a good match?",
    storyTitleLove: "How is that person seeing you right now?",
    storySubLove: "Analysis of emotional flow and optimal timing",
    storyTitleNewYear: "What will this year bring you?",
    storySubNewYear: "Monthly fortune flow and lucky keywords",
    storyTitleBusiness: "Is now the right time for business and investment?",
    storySubBusiness: "Pinpoint exactly when your wealth breaks through",
    storySubWealth: "Pinpoint exactly when your wealth breaks through",
    storyTitleCareer: "A job change or promotion — is now the right time?",
    storySubCareer: "In-depth analysis of career-shift and upward timing",
    storyTitleHealth: "Are you missing the signals your body is sending?",
    storySubHealth: "Analysis of constitution-based vulnerable periods and care",
    storyTitleComprehensive: "Your Saju — what current is it riding right now?",
    storySubComprehensive: "Analysis of your innate destiny and the life flow ahead",
    scenarioRejoin0: "There is a time when {ptName}'s heart opens again. It matters to find the golden window when both of your fortunes align best, and reach out naturally then.",
    scenarioRejoin1: "Don't rush. When the other person wants time alone, reaching out only backfires. Waiting for the right moment is the wisest strategy.",
    scenarioRejoin2: "A short, light hello is far more effective than a long letter. Start with a low-pressure approach.",
    scenarioMarry0: "There is a time when both of your fortunes align toward marriage. Check the year and month when {name}'s marriage luck is strongest.",
    scenarioMarry1: "With marriage, timing is everything. It's best to talk naturally when your partner wants to plan a future together.",
    scenarioMarry2: "Moving toward marriage after financial stability and trust are in place makes for a far more stable start.",
    scenarioDeep0: "We precisely analyze the two of you — your five-element balance and personality compatibility. You'll see the reasons for the attraction and the patterns where conflict arises.",
    scenarioDeep1: "You can grasp the core of long-term compatibility: whether you empower each other, or drain each other.",
    scenarioDeep2: "Knowing in advance when conflict intensifies and strengthening communication beforehand lets you keep the relationship far longer and healthier.",
    scenarioBiz0: "When wealth flows in and out differs for every Saju. The key is knowing when to move aggressively and when to cut spending.",
    scenarioBiz1: "Concentrate contracts and sales in months when wealth luck is strong; focus on cost control and risk reduction when it is weak.",
    scenarioBiz2: "A partnership should begin when your fortunes align. A partnership signed when fortunes clash can lead to losses.",
    scenarioCareer0: "The times when chances for promotion, a job change, or new employment arrive are set. Knowing and preparing for them in advance brings a far higher success rate.",
    scenarioCareer1: "Don't miss the moment to be recognized and leap forward at work. Career change happens when a prepared person meets the right luck.",
    scenarioCareer2: "Rather than a reckless job change, building results internally first and then seizing a better opportunity can be more rewarding in the long run.",
    payModuleLoading: "The payment module is still loading. Please refresh and try again.",
    paymentName: "Wooyeon Saju — {svc} key dates reveal",
    payProcessError: "Payment was completed, but an error occurred while processing. Please contact the administrator.",
    payFailed: "Payment failed: {msg}",
    tryAgain: "Please try again.",
    dateTitleRejoin: "When can you reconcile with {ptName}?",
    dateTitleMarry: "Your marriage golden time",
    dateTitleBusiness: "Best days to sign a wealth-bringing contract",
    dateTitleCareer: "Best days for passing exams and job changes",
    dateSubWithPartner: "{name}'s favorable element ({useGod}) + day-pillar harmony (合) with {ptName}",
    dateSubSelf: "Optimal timing based on {name}'s favorable element ({useGod})",
    emotionalConflictDays: "Emotional-clash danger days (breakup risk)",
    dateGanzhi: "{m}/{d}/{y} ({gan}{ji} day)",
    giChungDay: "— Day of concentrated clash (沖) with the unfavorable element ({avoidGod})",
    top3Rejoin: "Top 3 days most likely for reconciliation",
    top3Marry: "Top 3 best days for a marriage agreement",
    top3Business: "Top 3 best days for contracts and launches",
    top3Career: "Top 3 best days for hiring and promotion",
    dateYmd: "{m}/{d}/{y}",
    unlockAfterPayment: "Unlock after payment",
    dateActiveDesc: "{gan}{ji} day · favorable element ({useGod}) active",
    partnerDayHap: " · day-pillar harmony (合) with {ptName}",
    monthScoreKeyDate: "Monthly fortune score {score} · key date",
    favorableJobs: "Favorable occupations for your element ({useGod})",
    favorableBiz: "Favorable industries for your element ({useGod})",
    payCtaQuestion: "Curious about the exact year, month, and day for the dates above?",
    payCtaReveal: "All dates are revealed instantly after payment.",
    payButton: "Pay ₩9,900 to reveal the dates →",
    careerWood0: "Education & Lecturing",
    careerWood1: "Korean Medicine & Healthcare",
    careerWood2: "Publishing & Writing",
    careerWood3: "Design & Interiors",
    careerWood4: "Law & Counseling",
    careerFire0: "Media & Broadcasting",
    careerFire1: "IT & Software",
    careerFire2: "Food Service & Cafés",
    careerFire3: "Marketing & Advertising",
    careerFire4: "Entertainment & Arts",
    careerEarth0: "Real Estate & Construction",
    careerEarth1: "Finance & Investment",
    careerEarth2: "Distribution & Logistics",
    careerEarth3: "Civil Service & Administration",
    careerEarth4: "Agriculture & Food",
    careerMetal0: "Manufacturing & Machinery",
    careerMetal1: "Finance & Insurance",
    careerMetal2: "Police & Military",
    careerMetal3: "IT Hardware",
    careerMetal4: "Medical Devices",
    careerWater0: "Trade & Import/Export",
    careerWater1: "Tourism & Travel",
    careerWater2: "Pharma & Chemicals",
    careerWater3: "Research & R&D",
    careerWater4: "Philosophy & Metaphysics",
    bizWood0: "Education Business",
    bizWood1: "Interiors & Remodeling",
    bizWood2: "Publishing & Content",
    bizWood3: "Medical & Healthcare",
    bizWood4: "Environment & Eco",
    bizFire0: "Food & Beverage / Cafés",
    bizFire1: "IT & App Development",
    bizFire2: "Media & YouTube",
    bizFire3: "Beauty & Cosmetics",
    bizFire4: "Entertainment",
    bizEarth0: "Real Estate Brokerage",
    bizEarth1: "Construction",
    bizEarth2: "Franchise & Distribution",
    bizEarth3: "Food-Ingredient Wholesale",
    bizEarth4: "Consulting",
    bizMetal0: "Metal & Machinery Manufacturing",
    bizMetal1: "Precious Metals & Jewelry",
    bizMetal2: "IT Hardware",
    bizMetal3: "Security & Guarding",
    bizMetal4: "Law & Accounting",
    bizWater0: "Import & Trade",
    bizWater1: "Online Store",
    bizWater2: "Travel Agency & Tourism",
    bizWater3: "Alcohol & Beverages",
    bizWater4: "Logistics & Delivery",
    totalCompatScore: "Overall Compatibility Score",
    personalityCompat: "Personality Match",
    intimacyCompat: "Intimacy Match",
    scorePts: "{n} pts",
    bestMarriageTiming: "Best Marriage Timing",
    childrenFortune: "Children Fortune",
    firstChildTiming: "Possible timing for first child: {timing}",
    childGenderPrediction: "Predicted child gender: {gender}",
    ohaengCompat: "Five-Element Compatibility",
    ohaengAnalysis: "Five-Element Analysis",
    hapTransformApplied: "harmony & transformation applied",
    generation: "→ generates (生)",
    control: "→ controls (剋)",
    hannanjoseupCompat: "Heat/Cold & Dry/Moist Compatibility",
    hannanLabel: "{name} — Heat/Cold",
    joseupLabel: "{name} — Dry/Moist",
    hannanComplementary: "Heat/Cold: complementary ✓",
    hannanSimilar: "Heat/Cold: similar ⚠️",
    joseupComplementary: "Dry/Moist: complementary ✓",
    joseupSimilar: "Dry/Moist: similar ⚠️",
    themeLoveFull: "Love",
    themeMarryFull: "Marriage",
    themeBusinessFull: "Business",
    themeCareerFull: "Career",
    themeHealthFull: "Health",
    themeWealthFull: "Wealth",
    themeLove: "Love",
    themeMarry: "Marriage",
    themeBusiness: "Business",
    themeCareer: "Career",
    themeHealth: "Health",
    themeWealth: "Wealth",
    saeunDetail: "Yearly Fortune — Detailed Analysis",
    wolunTitle: "Monthly Fortune (Jan–Dec)",
    wolunTapHint: "· Tap a month for the daily calendar",
    weekSun: "Sun",
    weekMon: "Mon",
    weekTue: "Tue",
    weekWed: "Wed",
    weekThu: "Thu",
    weekFri: "Fri",
    weekSat: "Sat",
    ilunCalendar: "Daily calendar — {m}/{y}",
    ganziMonth: "({gan}{ji} month)",
    legendYongil: "Favorable day",
    legendJohuil: "Seasonal-balance day",
    legendGiil: "Unfavorable day",
    legendNeutral: "Neutral day",
    counselorComment: "Counselor's Comment",
    toMain: "Home",
    viewOtherReadings: "See other online readings",
    savePdf: "Save PDF",
  },
  footer: {
    lblBizName: "Company",
    valBizName: "Wooyeon",
    lblRep: "CEO",
    valRep: "Kim Na-yeon",
    lblBizNo: "Business Reg. No.",
    lblMailOrder: "Mail-Order Sales No.",
    valMailOrder: "No. 2026-Seoul-Yeongdeungpo-1388",
    lblContact: "Phone",
    lblAddress: "Address",
    valAddress: "Wooyeon Saju, 1F, 14 Dorim-ro 125ga-gil, Yeongdeungpo-gu, Seoul",
    lblEmail: "Email",
    linkTerms: "Terms of Service",
    linkPrivacy: "Privacy Policy",
    linkRefund: "Refund Policy",
  },
  city: {
    placeholder: "Enter a city (e.g. 서울특별시, 도쿄)",
    hintPre: "If you're unsure of your birth city, enter ",
    hintStrong: "'서울특별시' (Seoul)",
    hintPost: " or your country's capital. (City names use Korean.)",
  },
};

export const STRINGS: Record<Lang, Dict> = { ko, en };
